import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { inputStyle, buttonStyle } from '../styles';

const CATEGORY_LABEL: Record<string, string> = {
  nehnutelnosti: 'Nehnuteľnosti',
  stroje_vybavenie: 'Stroje a vybavenie',
  zasoby_tovar: 'Zásoby/tovar',
  firmy_podiely: 'Firmy/podiely',
  vozidla: 'Vozidlá',
  ostatne: 'Ostatné',
};

interface CompanySearchHit {
  ico: string;
  name: string;
  city: string | null;
}

// Fáza 2 inzertnej sekcie (2026-07-15) — form na vytvorenie inzerátu. Company picker
// znovu použije GET /companies/search?q= (rovnaký endpoint ako TopBar.tsx). Obrázky
// sa nahrávajú AŽ po vytvorení riadku (create-then-attach — multipart+JSON sa v
// jednom requeste nemiešajú jednoducho). Vždy jasne komunikuje "čaká na schválenie",
// nikdy neimplikuje, že je inzerát hneď live (viď listings.service.ts — status je
// vždy pending_review bez ohľadu na client input).
export function PostListingPage() {
  const { user, loading } = useAuth();

  const [category, setCategory] = useState('nehnutelnosti');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceEur, setPriceEur] = useState('');
  const [dohodou, setDohodou] = useState(false);
  const [city, setCity] = useState('');
  const [isInsolvencyRelated, setIsInsolvencyRelated] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [companyDebounced, setCompanyDebounced] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<CompanySearchHit | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user && !contactEmail) setContactEmail(user.email);
  }, [user, contactEmail]);

  useEffect(() => {
    const q = companyQuery.trim();
    if (q.length < 2) {
      setCompanyDebounced('');
      return;
    }
    const timer = setTimeout(() => setCompanyDebounced(q), 250);
    return () => clearTimeout(timer);
  }, [companyQuery]);

  const { data: companyResults } = useQuery<CompanySearchHit[]>({
    queryKey: ['company-search-for-listing', companyDebounced],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/search?q=${encodeURIComponent(companyDebounced)}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: companyDebounced.length >= 2,
  });

  if (loading) return null;
  if (!user) return <Navigate to="/prihlasenie" replace />;
  if (done) {
    return (
      <main className="page">
        <div className="fin-panel" style={{ padding: '24px 28px' }}>
          <h2>Inzerát bol odoslaný</h2>
          <p>
            Čaká na schválenie administrátorom — na verejnom zozname{' '}
            <strong>ešte nie je zobrazený</strong>. Stav sledujte na{' '}
            <a href="/inzeraty/moje">Moje inzeráty</a>.
          </p>
        </div>
      </main>
    );
  }

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/listings', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          category,
          title,
          description,
          priceCents: dohodou || !priceEur ? null : Math.round(Number(priceEur) * 100),
          city: city.trim() || null,
          companyIco: selectedCompany?.ico ?? null,
          isInsolvencyRelated,
          contactEmail,
          contactPhone: contactPhone.trim() || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? `${res.status} ${res.statusText}`);
      }
      const created = await res.json();
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await fetch(`/api/v1/listings/${created.id}/images`, { method: 'POST', body: formData });
      }
      setDone(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="page">
      <div className="page-header">
        <h1>Pridať inzerát</h1>
      </div>

      <div className="fin-panel" style={{ padding: '14px 20px', marginBottom: '1.5rem' }}>
        Corpuls <strong>neoveruje</strong> právo predať uvedený majetok. Ak inzerujete majetok
        súvisiaci s insolvenciou/konkurzom, uistite sa, že predaj je v súlade s predpísaným postupom
        (dražba/verejná ponuka cez správcu podľa zákona o konkurze a reštrukturalizácii) — táto
        platforma len sprostredkuje kontakt, nenahrádza právny proces. Po odoslaní inzerát čaká na
        schválenie, kým nie je verejne viditeľný.
      </div>

      {error && (
        <div
          className="fin-panel"
          style={{ padding: '12px 18px', marginBottom: '1rem', color: '#991b1b' }}
        >
          {error}
        </div>
      )}

      <form
        onSubmit={submit}
        className="fin-panel"
        style={{ padding: '20px 24px', display: 'grid', gap: 14 }}
      >
        <label>
          <div>Kategória</div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
            {Object.entries(CATEGORY_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <div>Titulok</div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={inputStyle}
            required
            minLength={3}
          />
        </label>

        <label>
          <div>Popis</div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ ...inputStyle, minHeight: 120 }}
            required
            minLength={10}
          />
        </label>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <label style={{ flex: 1 }}>
            <div>Cena (€)</div>
            <input
              value={priceEur}
              onChange={(e) => setPriceEur(e.target.value)}
              type="number"
              min="0"
              disabled={dohodou}
              style={inputStyle}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 10 }}>
            <input
              type="checkbox"
              checked={dohodou}
              onChange={(e) => setDohodou(e.target.checked)}
            />
            <span>Cena dohodou</span>
          </label>
        </div>

        <label>
          <div>Mesto / obec</div>
          <input value={city} onChange={(e) => setCity(e.target.value)} style={inputStyle} />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={isInsolvencyRelated}
            onChange={(e) => setIsInsolvencyRelated(e.target.checked)}
          />
          <span>Súvisí s insolvenciou/konkurzom</span>
        </label>

        <label style={{ position: 'relative' }}>
          <div>Súvisiaca firma (voliteľné)</div>
          {selectedCompany ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>
                {selectedCompany.name} · IČO {selectedCompany.ico}
              </span>
              <button type="button" onClick={() => setSelectedCompany(null)} style={buttonStyle}>
                Zrušiť
              </button>
            </div>
          ) : (
            <>
              <input
                value={companyQuery}
                onChange={(e) => setCompanyQuery(e.target.value)}
                placeholder="Hľadať firmu podľa názvu…"
                style={inputStyle}
              />
              {companyResults && companyResults.length > 0 && companyDebounced && (
                <ul
                  style={{
                    position: 'absolute',
                    zIndex: 5,
                    background: 'var(--bg-raised)',
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--radius)',
                    listStyle: 'none',
                    margin: 0,
                    padding: 4,
                    width: '100%',
                  }}
                >
                  {companyResults.map((c) => (
                    <li key={c.ico}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCompany(c);
                          setCompanyQuery('');
                          setCompanyDebounced('');
                        }}
                        style={{ ...buttonStyle, width: '100%', textAlign: 'left' }}
                      >
                        {c.name} · IČO {c.ico}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </label>

        <label>
          <div>Kontaktný e-mail</div>
          <input
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            type="email"
            style={inputStyle}
            required
          />
        </label>

        <label>
          <div>Kontaktný telefón (voliteľné)</div>
          <input
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            style={inputStyle}
          />
        </label>

        <label>
          <div>Fotky (max 8, JPEG/PNG/WebP)</div>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 8))}
          />
        </label>

        <button type="submit" disabled={submitting} style={buttonStyle}>
          {submitting ? 'Odosielam…' : 'Odoslať na schválenie'}
        </button>
      </form>
    </main>
  );
}
